import { useEffect, useRef, useState } from 'react';

interface BenefitItem {
  id: string;
  icon: string;
  title: string;
  description: string;
  iconBg?: string;
  color?: string;
}

interface BenefitsProps {
  items?: BenefitItem[];
  loading?: boolean;
}

export default function Benefits({ items = [], loading = false }: BenefitsProps) {
  const [isVisible, setIsVisible] = useState(false);
  const benefits = items; // Sử dụng items từ props
  const [animateProgress, setAnimateProgress] = useState(false);
  const [counted, setCounted] = useState(false);
  const [count10, setCount10] = useState(0);
  const [count100, setCount100] = useState(0);
  const [count85, setCount85] = useState(0);
  const [count100Percent, setCount100Percent] = useState(0);
  const sectionRef = useRef<HTMLElement>(null);
  const statsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          setTimeout(() => setAnimateProgress(true), 500);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (counted) return; // Đã chạy rồi thì không chạy nữa

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !counted) {
            setCounted(true);
            
            // Count up animation for 10-100x
            const duration = 2000;
            const steps = 100;
            let current10 = 0;
            let current100 = 0;
            const increment10 = 10 / steps;
            const increment100 = 100 / steps;
            
            const timer = setInterval(() => {
              current10 += increment10;
              current100 += increment100;
              
              if (current10 >= 10) {
                setCount10(10);
              } else {
                setCount10(Math.floor(current10));
              }
              
              if (current100 >= 100) {
                setCount100(100);
                clearInterval(timer);
              } else {
                setCount100(Math.floor(current100));
              }
            }, duration / steps);

            // Count up animation for 85%
            let current85 = 0;
            const increment85 = 85 / steps;
            const timer85 = setInterval(() => {
              current85 += increment85;
              if (current85 >= 85) {
                setCount85(85);
                clearInterval(timer85);
              } else {
                setCount85(Math.floor(current85));
              }
            }, duration / steps);

            // Count up animation for 100%
            let current100Percent = 0;
            const increment100Percent = 100 / steps;
            const timer100Percent = setInterval(() => {
              current100Percent += increment100Percent;
              if (current100Percent >= 100) {
                setCount100Percent(100);
                clearInterval(timer100Percent);
              } else {
                setCount100Percent(Math.floor(current100Percent));
              }
            }, duration / steps);

            // Disconnect observer sau khi đã chạy
            observer.disconnect();
          }
        });
      },
      { threshold: 0.3 }
    );

    if (statsRef.current) {
      observer.observe(statsRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, [counted]);

  return (
    <section ref={sectionRef} className="py-24 bg-cyan-50 relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0 animate-[drift_30s_ease-in-out_infinite]" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%2306b6d4' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }}></div>
      </div>

      <div className="max-w-[1280px] mx-auto px-6 relative z-10">
        {/* Header */}
        <div className={`text-center mb-16 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div className="inline-block px-4 py-2 bg-[#06b6d4]/10 rounded-full mb-6">
            <span className="text-[#06b6d4] text-sm font-semibold uppercase tracking-wider">Lợi ích vượt trội</span>
          </div>
          <h2 className="font-['Inter',sans-serif] text-4xl md:text-5xl font-bold text-[#1a202c] mb-6">
            Giá trị mang lại
          </h2>
          <p className="text-lg text-[#4a5568] max-w-3xl mx-auto">
            Đầu tư vào bảo mật không chỉ là chi phí, mà là khoản đầu tư thông minh bảo vệ tài sản và uy tín doanh nghiệp.
          </p>
        </div>

        {/* Benefits Grid */}
        {loading ? (
          <div className="text-center py-12 mb-16">
            <div className="inline-block w-16 h-16 border-4 border-[#06b6d4] border-t-transparent rounded-full animate-spin"></div>
            <p className="mt-4 text-gray-600">Đang tải...</p>
          </div>
        ) : benefits.length === 0 ? (
          <div className="text-center py-12 mb-16">
            <p className="text-gray-600">Chưa có nội dung</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-8 mb-16">
            {benefits.map((benefit, index) => (
              <div 
                key={benefit.id}
                className={`group relative bg-white rounded-2xl p-8 border-2 border-[#e2e8f0] hover:border-[#06b6d4] transition-all duration-500 hover:shadow-xl hover:-translate-y-2 ${
                  isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                }`}
                style={{ 
                  transitionDelay: isVisible ? `${index * 0.1}s` : '0s'
                }}
              >
                {/* Number badge */}
                <div className="absolute -top-4 -left-4 w-12 h-12 rounded-full bg-gradient-to-br from-[#06b6d4] to-[#0891b2] flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300">
                  <span className="text-white font-bold text-lg">{index + 1}</span>
                </div>

                {/* Icon */}
                <div className={`w-16 h-16 rounded-xl ${benefit.iconBg || 'bg-gradient-to-br from-[#06b6d4]/10 to-[#0891b2]/10'} flex items-center justify-center mb-6 group-hover:scale-110 group-hover:rotate-6 transition-all duration-300`}>
                  <i className={`${benefit.icon} text-3xl ${benefit.color || 'text-[#06b6d4]'}`}></i>
                </div>

                {/* Content */}
                <h3 className="font-['Inter',sans-serif] text-xl font-bold text-[#1a202c] mb-4 group-hover:text-[#06b6d4] transition-colors duration-300">
                  {benefit.title}
                </h3>
                <p className="text-[#4a5568] leading-relaxed">
                  {benefit.description}
                </p>
              </div>
            ))}
          </div>
        )}

        {/* Stats Bar */}
        <div ref={statsRef} className={`bg-white rounded-2xl p-10 border-2 border-[#e2e8f0] shadow-lg transition-all duration-1000 delay-500 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
        }`}>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center group">
              <div className="text-5xl font-bold text-[#06b6d4] mb-2 group-hover:scale-110 transition-transform duration-300">
                {counted ? `${count10}-${count100}x` : '0x'}
              </div>
              <div className="text-[#4a5568] font-medium mb-4">Tiết kiệm chi phí sửa lỗi</div>
              <div className="w-full h-2 bg-[#e2e8f0] rounded-full overflow-hidden">
                <div 
                  className="h-full bg-gradient-to-r from-[#06b6d4] to-[#0891b2] rounded-full transition-all duration-1000 ease-out"
                  style={{ width: animateProgress ? '95%' : '0%' }}
                ></div>
              </div>
            </div>
            <div className="text-center group">
              <div className="text-5xl font-bold text-[#06b6d4] mb-2 group-hover:scale-110 transition-transform duration-300">
                {count85}%
              </div>
              <div className="text-[#4a5568] font-medium mb-4">Giảm rủi ro bảo mật</div>
              <div className="w-full h-2 bg-[#e2e8f0] rounded-full overflow-hidden">
                <div 
                  className="h-full bg-gradient-to-r from-[#06b6d4] to-[#0891b2] rounded-full transition-all duration-1000 ease-out delay-200"
                  style={{ width: animateProgress ? '85%' : '0%' }}
                ></div>
              </div>
            </div>
            <div className="text-center group">
              <div className="text-5xl font-bold text-[#06b6d4] mb-2 group-hover:scale-110 transition-transform duration-300">
                {count100Percent}%
              </div>
              <div className="text-[#4a5568] font-medium mb-4">Tuân thủ quy định</div>
              <div className="w-full h-2 bg-[#e2e8f0] rounded-full overflow-hidden">
                <div 
                  className="h-full bg-gradient-to-r from-[#06b6d4] to-[#0891b2] rounded-full transition-all duration-1000 ease-out delay-400"
                  style={{ width: animateProgress ? '100%' : '0%' }}
                ></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}